/**
 * Angular parity: `import-data` → `CompanyChangesComponent` (“Detail Comparison” dialog).
 */
import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { OnScreenModal } from '@/components/ui/OnScreenModal'
import { formatPhoneDisplay } from '@/features/property/utils/formatPhone'
import apiClient from '@/lib/api/apiClient'
import { showApiError } from '@/lib/utils/notifications'
import type { AlnRow} from '../types/importData'
import { mapImportTypeToViewKind } from '../utils/alnImportViewKind'
import type { AlnImportViewCompanyApiRow, AlnImportViewDetailModalProps } from '../types'

type CompanyApiRow = AlnImportViewCompanyApiRow

function dash(v: unknown): string {
  if (v == null || v === '') return '—'
  return String(v)
}

function listedLabel(listed: unknown): string {
  return listed === '1' || listed === 1 ? 'Active' : 'Not Active'
}

function inviteLabel(pmc: unknown): string {
  return pmc === '1' || pmc === 1 ? 'Required' : 'Optional'
}

function policyLabel(policy: unknown): string {
  return policy === 'COMPANY' ? 'Exception' : 'Standard'
}

function charityLabel(ids: unknown): string {
  if (!Array.isArray(ids) || ids.length === 0) return ''
  return `${ids.length} charity`
}

function CompanyHalf({ title, detail }: { title: string; detail: CompanyApiRow | null }) {
  if (!detail) {
    return (
      <div className="text-sm text-slate-500">
        <p className="font-semibold text-slate-800">{title}</p>
        <p className="mt-2">No data.</p>
      </div>
    )
  }
  return (
    <div className="space-y-3 text-sm">
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">{title}</p>
      <p className="font-bold text-slate-900">{dash(detail.company_name)}</p>
      <Row label="Status" value={listedLabel(detail.listed)} />
      <Row label="Invites" value={inviteLabel(detail.pmc_invite)} />
      <Row label="Payments" value={dash(detail.payment_method)} />
      <Row label="Relationship" value={dash(detail.accountmanager && typeof detail.accountmanager === 'object' ? (detail.accountmanager as { name?: string }).name : '')} />
      <Row label="Charity" value={charityLabel(detail.listed_ids)} />
      <Row label="Default Policy" value={policyLabel(detail.default_policy)} />
      <Row label="Lead Time" value={dash(detail.lease_start_lead_days)} />
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-wrap gap-x-2">
      <span className="font-semibold text-slate-700">{label}:</span>
      <span className="text-slate-800">{value || '—'}</span>
    </div>
  )
}

function ManagerAreaBody({ row, kind }: { row: AlnRow; kind: 'manager_change' | 'area_supervisor' }) {
  const propertyLabel = dash(row.name ?? row.property_name)
  const companyName = dash(row.mgmt_company)
  if (kind === 'manager_change') {
    return (
      <div className="space-y-4 text-sm text-slate-800">
        <p>
          <span className="font-semibold">Property Name:</span> {propertyLabel}
        </p>
        <p>
          <span className="font-semibold">Company Name:</span> {companyName}
        </p>
        <div className="grid gap-4 md:grid-cols-2">
          <Row label="Old Email" value={dash(row.previous_manager_email)} />
          <Row label="New Email" value={dash(row.current_manager_email)} />
        </div>
        <div className="grid gap-4 md:grid-cols-2">
          <Row label="Previous Manager" value={dash(row.previous_manager_name)} />
          <Row label="Current Manager" value={dash(row.current_manager_name)} />
        </div>
      </div>
    )
  }
  return (
    <div className="space-y-4 text-sm text-slate-800">
      <p>
        <span className="font-semibold">Property Name:</span> {propertyLabel}
      </p>
      <p>
        <span className="font-semibold">Company Name:</span> {companyName}
      </p>
      <div className="grid gap-4 md:grid-cols-2">
        <Row label="Old Email" value={dash(row.previous_area_supervisor_email)} />
        <Row label="New Email" value={dash(row.current_area_supervisor_email)} />
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        <Row label="Previous A/S" value={dash(row.previous_area_supervisor_name)} />
        <Row label="Current A/S" value={dash(row.current_area_supervisor_name)} />
      </div>
    </div>
  )
}

function PmcImportBody({ row }: { row: AlnRow }) {
  const name = dash(row.current_pmc_name ?? row.mgmt_company)
  const addr = [row.current_pmc_address_1, row.current_pmc_city, row.current_pmc_state].filter(Boolean).join(' ')
  const phoneRaw = row.current_pmc_phone != null ? String(row.current_pmc_phone) : ''
  const phone = phoneRaw ? formatPhoneDisplay(phoneRaw) : ''
  return (
    <div className="space-y-3 text-sm text-slate-800">
      <p>
        <span className="font-semibold">Mgmt Company Name:</span> {name}
      </p>
      <p>
        <span className="font-semibold">Mgmt Company Address:</span> {addr || '—'}
      </p>
      <p>
        <span className="font-semibold">Mgmt Company Phone:</span> {phone || '—'}
      </p>
    </div>
  )
}

function PropertyImportBody({ row }: { row: AlnRow }) {
  const name = dash(row.name ?? row.current_property_name)
  const addr = [row.current_property_address, row.current_property_city, row.current_property_state].filter(Boolean).join(' ')
  const phoneRaw = row.current_property_phone != null ? String(row.current_property_phone) : ''
  const phone = phoneRaw ? formatPhoneDisplay(phoneRaw) : ''
  return (
    <div className="space-y-3 text-sm text-slate-800">
      <p>
        <span className="font-semibold">Property Name:</span> {name}
      </p>
      <p>
        <span className="font-semibold">Property Address:</span> {addr || '—'}
      </p>
      <p>
        <span className="font-semibold">Property Phone:</span> {phone || '—'}
      </p>
      <p>
        <span className="font-semibold">Property Email:</span> {dash(row.property_email)}
      </p>
    </div>
  )
}

export function AlnImportViewDetailModal({ open, onClose, row, importType }: AlnImportViewDetailModalProps) {
  const viewKind = mapImportTypeToViewKind(importType)
  const [oldCo, setOldCo] = useState<CompanyApiRow | null>(null)
  const [newCo, setNewCo] = useState<CompanyApiRow | null>(null)
  const [loadingCo, setLoadingCo] = useState(false)

  useEffect(() => {
    if (!open || !row || viewKind !== 'company_change') {
      setOldCo(null)
      setNewCo(null)
      setLoadingCo(false)
      return
    }
    const prevId = row.previous_company_id
    const currId = row.current_company_id
    let cancelled = false

    async function loadOne(id: unknown): Promise<CompanyApiRow | null> {
      if (id == null || String(id) === '' || String(id) === '0') return null
      try {
        const res = await apiClient.get<CompanyApiRow>(`/api/company/${id}`)
        return res.data ?? null
      } catch (e) {
        showApiError(e)
        return null
      }
    }

    async function load() {
      setLoadingCo(true)
      setOldCo(null)
      setNewCo(null)
      try {
        const [o, n] = await Promise.all([loadOne(prevId), loadOne(currId)])
        if (!cancelled) {
          setOldCo(o)
          setNewCo(n)
        }
      } finally {
        if (!cancelled) setLoadingCo(false)
      }
    }

    void load()
    return () => {
      cancelled = true
    }
  }, [open, row, viewKind])

  if (!open || !row) return null

  const title = 'Detail Comparison'

  const body = (() => {
    if (!viewKind) {
      return (
        <pre className="whitespace-pre-wrap break-all text-xs text-slate-600">{JSON.stringify(row, null, 2)}</pre>
      )
    }

    if (viewKind === 'company_change') {
      if (loadingCo) {
        return (
          <div className="flex items-center justify-center gap-2 py-8 text-slate-600">
            <Loader2 className="h-8 w-8 animate-spin text-button" aria-hidden />
            <span className="text-sm">Loading company details…</span>
          </div>
        )
      }
      return (
        <div className="grid gap-6 md:grid-cols-2">
          <CompanyHalf title="Previous" detail={oldCo} />
          <CompanyHalf title="New" detail={newCo} />
        </div>
      )
    }

    if (viewKind === 'manager_change') {
      return <ManagerAreaBody row={row} kind="manager_change" />
    }
    if (viewKind === 'area_supervisor') {
      return <ManagerAreaBody row={row} kind="area_supervisor" />
    }
    if (viewKind === 'pmc_import') {
      return <PmcImportBody row={row} />
    }
    if (viewKind === 'property_import') {
      return <PropertyImportBody row={row} />
    }
    return (
      <pre className="whitespace-pre-wrap break-all text-xs text-slate-600">{JSON.stringify(row, null, 2)}</pre>
    )
  })()

  return (
    <OnScreenModal
      isOpen={open}
      title={title}
      onClose={onClose}
      maxWidth="max-w-3xl"
      overlayDataTestId="aln-import-view-detail-overlay"
      panelDataTestId="aln-import-view-detail-panel"
      bodyClassName="max-h-[85vh] overflow-y-auto"
    >
      <div className="space-y-4">
        {body}
        <div className="flex justify-end pt-4 border-t border-slate-100">
          <button
            type="button"
            className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-800 hover:bg-slate-50"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </OnScreenModal>
  )
}
